// DataTable.jsx — reusable, config-driven, server-paginated table shell.
import React, { useState, useMemo, useRef, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { debounce } from "lodash";
import useTableData from "./useTableData";
import { getColumnType } from "./columnTypes";
import TableSettingsDropdown from "./TableSettingsDropdown";

const styles = `
  #dropdown-table-columns::after { display: none !important; }
  .dropdown-table-columns-width { width: 220px; }
  .dt-sortable { cursor: pointer; user-select: none; }
  .dt-skel {
    display: inline-block;
    height: 0.85rem;
    border-radius: 4px;
    background: linear-gradient(90deg, #eef0f4 25%, #f7f8fa 37%, #eef0f4 63%);
    background-size: 400% 100%;
    animation: dt-skel-wave 1.2s ease infinite;
  }
  @keyframes dt-skel-wave {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }
  .dt-sticky thead th {
    position: sticky;
    top: var(--dt-sticky-top, 0px);
    background: #fff;
    z-index: 2;
  }
  .dt-gear-th { width: 1%; white-space: nowrap; text-align: right; }
  .dt-expanded-row > td { background: #fbfcfe; padding: 0 !important; }
`;

// Deterministic width variation so skeleton rows read as "content shaped",
// not uniform bars.
const SKEL_WIDTHS = ["72%", "48%", "62%", "55%", "68%"];

export default function DataTable({ config, adapter, onRowClick, onMeta, clientRows, isLoading, renderExpanded }) {
   const {
      rows, loading, error, pagination, filters, sort, meta, quickFilter,
      setFilter, clearFilters, changeSearch, toggleSort, nextPage, prevPage, setPageSize, setQuickFilter,
   } = useTableData({ config, adapter, clientRows });
   const effectiveLoading = loading || isLoading;

   // Inline row expansion (opt-in via renderExpanded). Clicking a row toggles a
   // full-width detail strip beneath it instead of firing onRowClick.
   const [expandedId, setExpandedId] = useState(null);

   // Surface result metadata (e.g., total row count) to the host page.
   const onMetaRef = useRef(onMeta);
   useEffect(() => { onMetaRef.current = onMeta; });
   useEffect(() => {
      onMetaRef.current?.({ totalRows: pagination.totalRows });
   }, [pagination.totalRows]);

   const visibleDefaults = useMemo(
      () => config.columns.reduce((acc, c) => { acc[c.field] = c.defaultVisible !== false; return acc; }, {}),
      [config]
   );
   const [columnVisibility, setColumnVisibility] = useState(visibleDefaults);

   // dropdown temp state
   const [showDropdown, setShowDropdown] = useState(false);
   const [tempPageSize, setTempPageSize] = useState(pagination.pageSize);
   const [tempColumnVisibility, setTempColumnVisibility] = useState(columnVisibility);

   const handleToggleDropdown = (isOpen) => {
      setShowDropdown(isOpen);
      if (isOpen) {
         setTempPageSize(pagination.pageSize);
         setTempColumnVisibility(columnVisibility);
      }
   };
   const handlePageSizeChange = (size) => setTempPageSize(size);
   const handleColumnVisibilityChange = (field) =>
      setTempColumnVisibility((prev) => ({ ...prev, [field]: !(prev[field] ?? true) }));
   const handleApplyChanges = () => {
      setPageSize(tempPageSize);
      setColumnVisibility(tempColumnVisibility);
      setShowDropdown(false);
   };
   const handleCancelChanges = () => {
      setTempPageSize(pagination.pageSize);
      setTempColumnVisibility(columnVisibility);
      setShowDropdown(false);
   };

   // Controlled global search text + debounced query trigger so typing fires one request,
   // not one per keystroke, and "Clear Filters" actually empties the box.
   const [searchText, setSearchText] = useState("");
   const debouncedSearchRef = useRef(debounce((v) => changeSearch(v), 300));
   useEffect(() => {
      const d = debouncedSearchRef.current;
      return () => d.cancel();
   }, []);

   const handleClearAll = () => {
      setSearchText("");
      debouncedSearchRef.current.cancel();
      clearFilters();
   };

   const visibleColumns = config.columns.filter((c) => columnVisibility[c.field] !== false);
   const lastVisibleField = visibleColumns.length ? visibleColumns[visibleColumns.length - 1].field : null;

   // The column-settings cog, rendered either in the toolbar (default) or inline in the
   // header row (gearInHeader — Documents-style). align="end" anchors the menu to the
   // gear's right edge so it never overflows the viewport.
   const gear = (
      <TableSettingsDropdown
         showDropdown={showDropdown}
         handleToggleDropdown={handleToggleDropdown}
         tempPageSize={tempPageSize}
         handlePageSizeChange={handlePageSizeChange}
         columns={config.columns}
         tempColumnVisibility={tempColumnVisibility}
         handleColumnVisibilityChange={handleColumnVisibilityChange}
         handleApplyChanges={handleApplyChanges}
         handleCancelChanges={handleCancelChanges}
      />
   );

   return (
      <>
         <style>{styles}</style>
         {/* Toolbar shows whenever it has content (quick-filter tabs, search, or a gear that
             isn't in the header). gearInHeader only relocates the cog — it must NOT suppress
             the quick-filter tabs/Clear Filters that some pages (e.g. Suppliers) render here. */}
         {(config.quickFilter || config.showSearch !== false || !config.gearInHeader) && (
         <Row className="pt-2 pb-2">
            <Col className="d-flex align-items-center gap-2">
               {config.quickFilter && (
                  <div className="dt-segments" role="group" aria-label={config.quickFilter.label || "Quick filter"}>
                     {config.quickFilter.options.map((opt) => {
                        const active = quickFilter === opt.value;
                        const count = config.quickFilter.countsFrom && meta?.stateCounts ? meta.stateCounts[opt.value] : null;
                        return (
                           <button key={opt.value} type="button"
                                   className={`dt-segment ${active ? "active" : ""}`}
                                   onClick={() => setQuickFilter(opt.value)}>
                              {opt.label}
                              {count != null && <span className="dt-segment-count">{Number(count).toLocaleString()}</span>}
                           </button>
                        );
                     })}
                  </div>
               )}
               {config.showSearch !== false && (
                  <input
                     className="form-control"
                     style={{ maxWidth: 280 }}
                     type="text"
                     placeholder="Search…"
                     value={searchText}
                     onChange={(e) => { setSearchText(e.target.value); debouncedSearchRef.current(e.target.value); }}
                  />
               )}
               <button type="button" onClick={handleClearAll} className="btn btn-outline-secondary rounded-pill pt-1 pb-1">
                  Clear Filters
               </button>
               {!config.gearInHeader && <span className="ms-auto">{gear}</span>}
            </Col>
         </Row>
         )}
         <Row className="g-3 g-lg-4">
            <Col>
               <div className="position-relative d-flex mb-2">
                  <table
                     className={`table ${config.stickyHeader ? "dt-sticky" : ""}`}
                     style={config.stickyHeader ? { "--dt-sticky-top": `${config.stickyHeaderTop || 0}px` } : undefined}
                  >
                     <thead>
                        <tr>
                           {visibleColumns.map((c) => {
                              const t = getColumnType(c.type);
                              const isSorted = sort && sort.field === c.field;
                              const caret = isSorted ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
                              const FilterComponent = c.filterable ? t.FilterComponent : null;
                              const gearHere = config.gearInHeader && c.field === lastVisibleField;
                              const headerLabel = (
                                 <span
                                    className={c.sortable ? "dt-sortable" : ""}
                                    onClick={c.sortable ? () => toggleSort(c.field) : undefined}
                                 >
                                    {c.label}{caret}
                                 </span>
                              );
                              return (
                                 <th key={c.field} className={gearHere ? "dt-gear-th" : undefined} style={{ textAlign: t.align }}>
                                    {gearHere ? (
                                       // Gear sits BESIDE the column's own label (Documents-style),
                                       // never replacing it — otherwise a labeled last column (e.g.
                                       // Suppliers' "Other names") would lose its header.
                                       <span className="d-inline-flex align-items-center gap-2">
                                          {headerLabel}
                                          {gear}
                                       </span>
                                    ) : headerLabel}
                                    {FilterComponent && (
                                       <FilterComponent
                                          column={{ id: c.field, value: filters[c.field] }}
                                          handleInputFilterChangeNoDebounce={setFilter}
                                          constants={c.enumValues || []}
                                       />
                                    )}
                                 </th>
                              );
                           })}
                        </tr>
                     </thead>
                     <tbody>
                        {effectiveLoading ? (
                           // One skeleton row per expected data row keeps the table
                           // height stable across page flips (no collapse/jump).
                           Array.from({ length: pagination.pageSize || 10 }, (_, i) => (
                              <tr key={`skel-${i}`} aria-hidden="true">
                                 {visibleColumns.map((c, j) => (
                                    <td key={c.field}>
                                       <span className="dt-skel" style={{ width: SKEL_WIDTHS[(i + j) % SKEL_WIDTHS.length], maxWidth: 180 }} />
                                    </td>
                                 ))}
                              </tr>
                           ))
                        ) : error ? (
                           <tr><td colSpan={visibleColumns.length} className="text-danger">{error}</td></tr>
                        ) : rows.length === 0 ? (
                           <tr><td colSpan={visibleColumns.length}>No records found…</td></tr>
                        ) : (
                           rows.map((row, idx) => {
                              const rowId = row[config.rowIdField] ?? idx;
                              const expandable = !!renderExpanded;
                              const isExpanded = expandable && expandedId === rowId;
                              const clickable = expandable || !!onRowClick;
                              const handleRowClick = expandable
                                 ? () => setExpandedId((prev) => (prev === rowId ? null : rowId))
                                 : onRowClick
                                 ? () => onRowClick(row)
                                 : undefined;
                              return (
                                 <React.Fragment key={rowId}>
                                    <tr
                                       onClick={handleRowClick}
                                       style={clickable ? { cursor: "pointer" } : undefined}
                                    >
                                       {visibleColumns.map((c) => {
                                          const t = getColumnType(c.type);
                                          return (
                                             <td key={c.field} style={{ textAlign: c.align || t.align }}>
                                                {(c.renderCell || t.renderCell)(row[c.field], row, c)}
                                             </td>
                                          );
                                       })}
                                    </tr>
                                    {isExpanded && (
                                       <tr className="dt-expanded-row">
                                          <td colSpan={visibleColumns.length}>{renderExpanded(row)}</td>
                                       </tr>
                                    )}
                                 </React.Fragment>
                              );
                           })
                        )}
                     </tbody>
                  </table>
               </div>
            </Col>
         </Row>
         <Row className="g-3 g-lg-4">
            <Col>
               {rows.length === 0 ? null : (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                     <div className="d-flex justify-content-center flex-grow-1">
                        <span>Page <strong>{pagination.pageIndex + 1}</strong> of {pagination.totalPages || 1}</span>
                     </div>
                     <div className="d-flex justify-content-end">
                        <button className="btn btn-primary me-2" onClick={prevPage} disabled={pagination.pageIndex === 0}>Previous</button>
                        <button className="btn btn-primary" onClick={nextPage} disabled={pagination.pageIndex >= pagination.totalPages - 1}>Next</button>
                     </div>
                  </div>
               )}
            </Col>
         </Row>
      </>
   );
}
